import { z } from "zod";
import type {
  FindUserTravelersOutputData,
  InitiateHotelsSearchOutputData,
  InitiateFlightsSearchOutputData,
  FindUserTravelersTravelers,
  FindHotelOffersRoom,
  FindUserReservationsFlightReservation,
  FindHotelOffersOutputData,
  FindHotelOffersPackageRoomOption,
  FindUserReservationsHotelReservation,
  FindCitiesCityWithAirports,
  FindCitiesAirport,
  FindCitiesCity,
  FindFlightOffersOffer,
} from "./codegen";
import { SortOrder, HotelsSortOptions, FlightsSortOptions } from "./enums";
import type { FlightFormValuesSchema } from "@/app/ui/flights/validation";
import type { HotelFormValuesSchema } from "@/app/ui/hotels/validation";

export type TravellerAgeGroup = "ADT" | "CHD" | "INF";
export const allTravellerAgeGroups: TravellerAgeGroup[] = ["ADT", "CHD", "INF"];

export type Traveller = FindUserTravelersTravelers;

export type SearchScope = "flights" | "hotels" | "trips";

export type Airport = Pick<FindCitiesAirport, "id" | "name" | "code"> & {
  cityName: string;
  countryName: string;
  type: "AIRPORT";
};

export type City = Pick<
  FindCitiesCity,
  "id" | "name" | "code" | "countryName" | "countryCode"
> & {
  type: "CITY";
};

export type CityWithAirports = Pick<
  FindCitiesCityWithAirports,
  "id" | "name" | "code" | "countryName" | "countryCode"
> & {
  airports: Airport[];
  type: "CITY";
};

export type FlightFormLocation = Airport | City;

export type TFlightFormFieldValues = z.input<FlightFormValuesSchema>;
export type TFlightFormValues = z.infer<FlightFormValuesSchema>;

export type TravellerFormValues = {
  firstName: string;
  lastName: string;
  birthDate: Date | null;
  passportNumber: string;
  emailAddress: string;
  passportIssuingDate: Date | null;
  passportExpiryDate: Date | null;
  title: "mr" | "mrs" | "miss" | "ms" | "mstr";
  phonePrefix: string;
  phoneNumber: string;
  passportNationality: "IQ";
  passportIssuingCountry: "IQ";
};

export type TRoom = {
  adults: number;
  children: number;
  childrenAges: (number | string)[];
};

export type CustomerEligibilityOutput =
  | {
      isEligible: true;
      sessionId: string;
      minCountOfMonth: number;
      maxCountOfMonth: number;
    }
  | {
      isEligible: false;
      errorCode?: number;
    };

export type DocumentScanningResult = {
  data: {
    givenName?: string;
    surName?: string;
    dateOfBirth?: string;
    gender?: string;
    nationality?: string;
    passportNumber?: string;
    dateOfIssue?: string;
    dateOfExpiry?: string;
  };
};
export type THotelFormFieldValues = z.input<HotelFormValuesSchema>;
export type THotelFormValues = z.infer<HotelFormValuesSchema>;

export type THotelSearchResult = {
  id: number | string;
  name: string;
  address: string;
  specialDeal?: boolean;
  recommended?: boolean;
  descriptions?: {
    short: string;
    full: string;
    location: string;
    facilities: string;
    rooms: string;
    essentialInformation: string;
  };
  boardBasis?: string[];
  stars: number;
  mainImageUrl: string;
  imagesUrls: string[];
  telephone?: string;
  email?: string;
  fax?: string;
  minPrice?: {
    value: number;
    currency: string;
  };
  geolocation?: {
    latitude: number;
    longitude: number;
  };
};

export type HotelSearchTokens = InitiateHotelsSearchOutputData;
export type FlightSearchTokens = InitiateFlightsSearchOutputData;

type HotelOptionalKeys =
  | "telephone"
  | "email"
  | "fax"
  | "geolocation"
  | "address"
  | "imagesUrls";

export type Hotel = Omit<THotelSearchResult, HotelOptionalKeys> &
  Partial<Pick<THotelSearchResult, HotelOptionalKeys>>;
export type THotelRoom = {
  name: string;
  board: string;
} & FindHotelOffersRoom;

export type TRoomOutput = {
  packageRoomToken: string;
  travelers: FindUserTravelersOutputData[];
};

export interface Price {
  value: number;
  currency: string;
}

export type HotelBookingRoomOption = FindHotelOffersPackageRoomOption;

export type HotelOfferRoom = FindHotelOffersRoom;

export type HotelOffer = FindHotelOffersOutputData;

export interface HotelBooking {
  packageToken: string;
  offerId: string;
  price?: Price;
  rooms: HotelBookingRoomOption[];
  availabilityToken?: string;
}

export type SortValue<T extends string> = {
  order: SortOrder;
  field: T;
};

export type HotelsSortValue = SortValue<HotelsSortOptions>;
export type FlightsSortValue = SortValue<FlightsSortOptions>;

export enum ValidationExceptionCode {
  CannotContainGivenPattern = "VALIDATION_VALUE_CANNOT_CONTAIN_GIVEN_PATTERN",
  IsDateString = "VALIDATION_VALUE_IS_DATE_STRING",
  MinimumLength = "VALIDATION_VALUE_MINIMUM_LENGTH",
  MustBeOneOf = "VALIDATION_VALUE_MUST_BE_ONE_OF",
}

export type ApiError = {
  code: string;
  data: {
    validationExceptionCode: ValidationExceptionCode;
    property: string;
    message: string;
  }[];
  message: string;
  status: string;
};

export type ReservationStatus =
  | FindUserReservationsFlightReservation["status"]
  // we want to be explicit here
  // eslint-disable-next-line @typescript-eslint/no-duplicate-type-constituents
  | FindUserReservationsHotelReservation["status"];

export type ServiceType = "air" | "hotel" | "trip";
export type SnackbarMessage = {
  type?: "success" | "error" | "warning" | "info";
  content: string;
};

export type TravellerUpdatedEventData = {
  travellerOldData: Traveller;
  travellerNewData: Traveller;
};

export type TravellerRemovedEventData = {
  travellerId: string;
};

export type TravellerDeselectedEventData = {
  travellerId: string;
};

export type OfferSelectedEventData = {
  offerType: ServiceType;
};

export type FlightOffer = FindFlightOffersOffer & {
  searchResultKey: string;
  searchResultToken: string;
};

export type FlightSearchType = "quick" | "full";
